import * as bookmarkConstants from "../../../../constants/bookmarkConstants";
import * as modalTypes from "../../../../constants/modalTypes";
import * as objectTypes from "../../../../constants/objectTypes";
import * as optionHelper from "../../../../helpers/optionHelper";
import * as priceHelper from "../../../../helpers/priceHelper";
import * as propertyConstants from "../../../../constants/propertyConstants";
import * as translationHelper from "../../../../helpers/translationHelper";

import {
    BaseInProduct,
    generateBaseInProductData,
    generateBaseInProductHeadings
} from "../../../../types/baseInProduct";
import React, { Component } from "react";

import { Bookmark } from "../../../../types/bookmark";
import { Currency } from "../../../../types/currency";
import { MenuItem } from "../../../../types/menu";
import { ModalParams } from "../../../../types/modalParams";
import { ModalProperties } from "../../../../types/modalProperties";
import { Product } from "../../../../types/product";
import { TableContainer } from "../../../Table";
import { TableHeading } from "../../../../types/tableHeading";

import { t as translate } from "react-i18nify";

type Props = {
    tableConstant: string;
    objectType: objectTypes.ObjectType;
    dataPrivileges: any;
    priceDataPrivileges: MenuItem | null;
    loading: boolean;
    buttonLoading: boolean;
    productActiveList: Array<Product>;
    allList: Array<BaseInProduct>;
    activeList: Array<BaseInProduct>;
    availableCount: number | null;
    columnOrder: Array<propertyConstants.Property>;
    columnVisibility: Record<propertyConstants.Property, boolean>;
    columnWidth: Record<propertyConstants.Property, number>;
    offset: number;
    page: number;
    rowCount: number;
    rowCountCustom: boolean;
    search: string | null;
    searchParams: Record<propertyConstants.Property, string>;
    showFilterRow: boolean;
    showGlobalSearch: boolean;
    sortingAsc: boolean;
    sortingCriterion: propertyConstants.Property | null;
    totalCount: number | null;
    useTmcNames: boolean;
    currency: Currency | null;
    optionPriceDefinition: string | null;
    optionPriceEnableCompanyCost: boolean;
    optionPriceEnablePriceGroups: boolean;
};

export class SystemProductBaseTable extends Component<Props> {
    generateTableHeadings = (
        modalType: modalTypes.ModalType | null = null,
        isPropagating: boolean = false,
        isPriceBookmark: boolean = false,
        isCheckColorantAmountBookmark: boolean = false
    ): Array<TableHeading> => {
        const pricePropertyList = priceHelper.getPriceProperties(
            this.props.objectType,
            modalType,
            this.props.priceDataPrivileges,
            this.props.optionPriceEnableCompanyCost,
            this.props.optionPriceEnablePriceGroups,
            this.props.optionPriceDefinition
        );
        const checkColorantAmountList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_IS_GRAVIMETRIC_FILL,
            propertyConstants.PROPERTY_COLORANT_MIN_FILL,
            propertyConstants.PROPERTY_COLORANT_MAX_FILL
        ];
        const propertyList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_CODE,
            propertyConstants.PROPERTY_COEFFICIENT,
            propertyConstants.PROPERTY_DENSITY,
            propertyConstants.PROPERTY_FILL_PROC,
            propertyConstants.PROPERTY_BFS,
            propertyConstants.PROPERTY_UFI_CODE,
            propertyConstants.PROPERTY_FORCED_FORMULA_BASE_AMOUNT_TYPE_ID,
            propertyConstants.PROPERTY_CUSTOM_FORMULA,
            propertyConstants.PROPERTY_MORE_INFO,
            propertyConstants.PROPERTY_UUID,
            propertyConstants.PROPERTY_PRIORITY
        ];
        const requiredList: Array<propertyConstants.Property> = [
            propertyConstants.PROPERTY_BASE_ID || propertyConstants.PROPERTY_BASE_NAME,
            propertyConstants.PROPERTY_FILL_PROC,
            propertyConstants.PROPERTY_PRODUCT_NAME
        ];
        const alwaysVisibleList: Array<propertyConstants.Property> = [propertyConstants.PROPERTY_NAME];
        let editableList: Array<propertyConstants.Property> = [...propertyList];
        let visibleList: Array<propertyConstants.Property> = [...propertyList];

        if (modalType === null) {
            visibleList.splice(9, 0, propertyConstants.PROPERTY_FILES);
            visibleList = visibleList.concat(checkColorantAmountList, pricePropertyList);
            editableList = editableList.concat(checkColorantAmountList, pricePropertyList);
        }

        visibleList.splice(9, 0, propertyConstants.PROPERTY_IMAGE_ID);

        if (modalType !== null) {
            editableList.push(propertyConstants.PROPERTY_IMAGE_ID);
        }

        if (modalType === modalTypes.MODAL_BASE_IN_PRODUCT_ADD) {
            visibleList.splice(0, 0, propertyConstants.PROPERTY_BASE_ID);
            editableList.push(propertyConstants.PROPERTY_BASE_ID);
        }

        if (modalType === null || modalType === modalTypes.MODAL_BASE_IN_PRODUCT_EDIT) {
            visibleList.splice(0, 0, propertyConstants.PROPERTY_BASE_NAME);
        }

        if (isPropagating) {
            visibleList = [propertyConstants.PROPERTY_BASE_NAME];
            editableList = [];
        }

        if (isPriceBookmark) {
            visibleList = pricePropertyList;
            editableList = pricePropertyList;
        }

        if (isCheckColorantAmountBookmark) {
            visibleList = checkColorantAmountList;
            editableList = checkColorantAmountList;
        }

        let orderList = [...visibleList];

        if (modalType === null) {
            orderList = optionHelper.handleTableHeadingOrder(visibleList, this.props.columnOrder);
            visibleList = optionHelper.handleTableHeadingVisibility(visibleList, this.props.columnVisibility);
        }

        return generateBaseInProductHeadings(
            this.props.objectType,
            [],
            orderList,
            visibleList,
            editableList,
            requiredList,
            alwaysVisibleList,
            this.props.columnWidth
        );
    };

    generateBookmarks = (modalType: modalTypes.ModalType): Array<Bookmark> => {
        return [
            new Bookmark(
                bookmarkConstants.BOOKMARK_GENERAL,
                bookmarkConstants.BOOKMARK_TYPE_FORM,
                translate("general.general"),
                this.generateTableHeadings(modalType),
                this.props.dataPrivileges, // TODO own privilege
                false
            ),
            new Bookmark(
                bookmarkConstants.BOOKMARK_PRICES,
                bookmarkConstants.BOOKMARK_TYPE_FORM,
                translate("prices.prices"),
                this.generateTableHeadings(modalType, false, true),
                this.props.dataPrivileges, // TODO own privilege
                false
            ),
            new Bookmark(
                bookmarkConstants.BOOKMARK_FILES,
                bookmarkConstants.BOOKMARK_TYPE_TABLE,
                translate("product.productFiles"),
                [],
                this.props.dataPrivileges, // TODO own privilege
                false
            ),
            new Bookmark(
                bookmarkConstants.BOOKMARK_COLORANTS_AMOUNT,
                bookmarkConstants.BOOKMARK_TYPE_FORM,
                translate("product.checkColorantsFilling"),
                this.generateTableHeadings(modalType, false, false, true),
                this.props.dataPrivileges,
                false
            )
        ];
    };

    generateAddModalParams = (): ModalParams => {
        return new ModalParams(
            modalTypes.ADD_MODAL,
            objectTypes.MASTER_SYSTEM_BASE_IN_PRODUCT,
            "",
            [],
            this.generateTableHeadings(modalTypes.MODAL_SYSTEM_BASE_IN_PRODUCTS_PROPAGATE, true),
            undefined,
            this.generateTableHeadings(modalTypes.MODAL_SYSTEM_BASE_IN_PRODUCTS_PROPAGATE, true),
            undefined,
            undefined,
            this.props.dataPrivileges,
            undefined,
            "simple-table"
        );
    };

    generateEditModalParams = (): ModalParams | null => {
        const activeData = generateBaseInProductData(this.props.activeList);

        if (activeData.length) {
            return new ModalParams(
                modalTypes.EDIT_MODAL,
                this.props.objectType,
                translate("product.editBaseInProduct"),
                activeData[0],
                [],
                this.generateBookmarks(modalTypes.MODAL_BASE_IN_PRODUCT_EDIT),
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                this.props.currency
            );
        }

        return null;
    };

    render(): JSX.Element {
        const modalProperties: ModalProperties = {
            addModalType: modalTypes.MODAL_SYSTEM_BASE_IN_PRODUCTS_PROPAGATE,
            addModalParams: this.generateAddModalParams(),
            editModalType: modalTypes.MODAL_BASE_IN_PRODUCT_EDIT,
            editModalParams: this.generateEditModalParams()
        };

        return (
            <div className="height-100">
                <TableContainer
                    className={"table-with-filter"}
                    tableConstant={this.props.tableConstant}
                    objectType={this.props.objectType}
                    dataPrivileges={this.props.dataPrivileges}
                    loading={this.props.loading}
                    buttonLoading={this.props.buttonLoading}
                    title={translationHelper.getTitleSectionTranslation(this.props.objectType)}
                    titleClassName={"title"}
                    headings={this.generateTableHeadings()}
                    data={generateBaseInProductData(this.props.allList)}
                    activeData={this.props.activeList}
                    availableCount={this.props.availableCount}
                    modalProperties={modalProperties}
                    offset={this.props.offset}
                    page={this.props.page}
                    rowCount={this.props.rowCount}
                    rowCountCustom={this.props.rowCountCustom}
                    search={this.props.search}
                    searchParams={this.props.searchParams}
                    showFilterRow={this.props.showFilterRow}
                    showGlobalSearch={this.props.showGlobalSearch}
                    sortingAsc={this.props.sortingAsc}
                    sortingCriterion={this.props.sortingCriterion}
                    totalCount={this.props.totalCount}
                    currency={this.props.currency}
                />
            </div>
        );
    }
}
